import React, { Component } from 'react'
import { Button, FormCheck, Form, Alert } from 'react-bootstrap'
import ReactHtmlParser from 'react-html-parser'
import Dialog from '../Dialog'
import { QuizAnswerType, QuizChoice, Quiz } from '../../Types'

interface Props {
    quiz: Quiz
    setQuizComplete: Function
}

interface State {
    radioSelected: string
    checkedItems: string[]
    formValid: boolean
    showDialog: boolean
    dialogTitle: string
    dialogBody: string
}

export default class PanelQuiz extends Component<Props, State> {

    state: State = {
        radioSelected: '',
        checkedItems: [],
        formValid: true,
        showDialog: false,
        dialogTitle: '',
        dialogBody: ''
    }

    private setDialogShow = (value: boolean) => {
        this.setState({ showDialog: value })
    }

    onSubmit = (e: any) => {
        e.preventDefault()
        const { checkedItems, radioSelected, formValid } = this.state
        const { type } = this.props.quiz


        if ((type === QuizAnswerType.SingleChoice && radioSelected === '')
            || (type === QuizAnswerType.MultipleChoice && checkedItems.length === 0)) {
            // A failsafe in case the button is enabled when it shouldn't be
            this.setFormValidity(false)
            return
        } else {
            !formValid && this.setFormValidity(true)
        }
        this.verifyAnswers()
    }

    private getSelectedChoices = (): any[] => {
        const selectedChoices: any[] = []
        for (const i of this.state.checkedItems) {
            const choice = this.props.quiz.choices!.find((item: any) => item.id === +i)
            selectedChoices.push(choice)
        }
        return selectedChoices
    }

    private verifyAnswers = () => {
        const { type } = this.props.quiz

        const correctAnswers: any[] = this.getAnswers(true)
        if (type === QuizAnswerType.SingleChoice) {
            if (!correctAnswers || correctAnswers.length < 1)
                // TODO: Test this
                throw new Error('Quiz question misconfiguration.')

            const isCorrect: boolean = this.state.radioSelected === correctAnswers[0].id.toString()
            this.handleAnswer(isCorrect, false)
            return
        }

        const selectedChoices = this.getSelectedChoices()
        const incorrectAnswers: any[] = this.getAnswers(false)


        const allOf: boolean = correctAnswers.length === this.props.quiz.choices!.length

        if (allOf) {
            selectedChoices.length === correctAnswers.length
                ? this.handleAnswer(true, false)
                : this.handleAnswer(false, true)

        }

        if (!allOf) {
            let incorrects: number = 0
            for (const i of selectedChoices) {
                if (incorrectAnswers.includes(i)) {
                    ++incorrects
                    const feedback: string = this.getIncorrectAnswerFeedback(i.id)
                    this.handleAnswer(false, false, feedback)
                    break
                }
            }
            if (incorrects === 0) {
                selectedChoices.length === correctAnswers.length
                    ? this.handleAnswer(true, false)
                    : this.handleAnswer(false, true)
            }
        }
    }

    private handleAnswer = (isCorrect: boolean, isIncomplete: boolean, incorrectFeedback?: string) => {
        const { setQuizComplete } = this.props
        const incompleteFeedback: string = this.props.quiz.feedback!.incomplete
        if (isCorrect) {
            setQuizComplete(true)
            this.showFeedback(isCorrect)
        }
        else {
            setQuizComplete(false)
            isIncomplete
                ? this.showFeedback(isCorrect, incompleteFeedback)
                : this.showFeedback(isCorrect, incorrectFeedback)
        }
    }

    private getIncorrectAnswerFeedback = (choiceID: string): string => {
        const selectedChoice = this.props.quiz.choices!.find((choice: any) => choice.id === +choiceID)
        const feedback: string = selectedChoice!.feedback.incorrect
        return feedback
    }

    private getAnswers(isCorrect: boolean): any[] {
        return this.props.quiz.choices!.filter((i: any) => i.isCorrect === isCorrect)
    }

    private showFeedback(isCorrect: boolean, incorrectFeedback?: string) {
        const { type } = this.props.quiz

        const title = isCorrect
            ? "<div class='font-weight-bold text-success'>You are correct!</div>"
            : incorrectFeedback === this.props.quiz.feedback!.incomplete
                ? "<div class='font-weight-bold text-warning'>Incomplete answer.</div>"
                : "<div class='font-weight-bold text-danger'>This is not correct.</div>"
        const body = isCorrect
            ? this.props.quiz.feedback!.correct
            : type === QuizAnswerType.SingleChoice
                ? this.getIncorrectAnswerFeedback(this.state.radioSelected)
                : incorrectFeedback ? incorrectFeedback : "<div class='alert alert-danger'>Undefined feedback message.</div>"
        this.showDialog(title, body)
    }

    private showDialog(title: string, body: string) {
        this.setState({
            dialogTitle: title,
            dialogBody: body,
            showDialog: true
        })
    }

    private setFormValidity = (validity: boolean) => {
        this.setState({
            formValid: validity
        })
    }

    onChange = (e: any) => {
        const element = e.target
        const { checkedItems, radioSelected } = this.state
        if (element.type === 'checkbox') {
            const items: any[] = checkedItems
            let index: number
            if (element.checked) {
                items.push(element.value)
            }
            else {
                index = items.indexOf(element.value)
                items.splice(index, 1)
            }
            this.setState({ checkedItems: items }, () => {
                checkedItems.length > 0 && this.setFormValidity(true)
            })
        }
        else {
            this.setState({ radioSelected: element.value }, () => {
                radioSelected !== '' && this.setFormValidity(true)
            })
        }
    }

    componentDidUpdate = (prevProps: Props) => {
        const { quiz, setQuizComplete } = this.props
        if (quiz !== prevProps.quiz) {
            this.setState({
                radioSelected: '',
                checkedItems: []
            }, setQuizComplete(false))
        }
    }

    render() {
        const { formValid, dialogTitle, dialogBody, showDialog } = this.state
        const { quiz } = this.props

        const choices: QuizChoice[] = quiz.choices!
        const type = quiz.type === QuizAnswerType.SingleChoice ? 'radio' : 'checkbox'

        const buttonDisabled: boolean =
            type === 'radio'
                ? this.state.radioSelected === ''
                : this.state.checkedItems.length === 0

        const quizChoices = choices.map((choice: QuizChoice) => (
            {
                key: choice.id,
                value: choice.id.toString(),
                label: choice.text,
                isCorrect: choice.isCorrect,
                feedback: choice.feedback
            }
        ))

        return (
            <div className="panel-quiz">
                <Form
                    onSubmit={this.onSubmit}
                >
                    {ReactHtmlParser(quiz.text!)}
                    <div className="mb-3 mt-3">
                        <div className='checkbox-list'>
                            {
                                quizChoices.map((choice: any) => {
                                    const isSelected: boolean = type === 'checkbox'
                                        ? this.state.checkedItems.filter((i: any) => i === choice.value).length === 1
                                        : this.state.radioSelected === choice.value

                                    return (
                                        <FormCheck
                                            key={choice.key}
                                            type={type}
                                            name={choice.label}
                                            label={choice.label}
                                            value={choice.value}
                                            checked={isSelected}
                                            onChange={(e: any) => this.onChange(e)}
                                        />
                                    )
                                })
                            }

                        </div>
                    </div>
                    {
                        !formValid && (
                            <Alert variant="danger">
                                You must select at least one answer.
                            </Alert>
                        )
                    }
                    <div className='button-bar'>
                        <Button
                            className="button primary"
                            type="submit"
                            disabled={buttonDisabled}
                        >
                            OK
                        </Button>
                    </div>
                </Form>

                <Dialog
                    title={dialogTitle}
                    body={dialogBody}
                    show={showDialog}
                    onHide={() => this.setDialogShow(false)}
                />
            </div>
        )
    }
}
